import React, { type PropsWithChildren, useContext } from "react"
import type { PrivateRouteProps } from "./PrivateRoute"
import type { OverrideAuthorizationFn } from "./types"

interface PrivateRouteContextState {
  /** Whether user is authenticated */
  isAuthenticated: boolean
  /** Return list of user permissions */
  getAllPermissions: () => string[]
  /** Override the authorization process. Return true/false to override, or null to skip. */
  overrideAuthorization?: OverrideAuthorizationFn
  /** Component to render on not authenticated */
  notAuthenticatedComponent: (props: PrivateRouteProps) => React.ReactNode
  /** Component to render on not authorized */
  notAuthorizedComponent: (props: PrivateRouteProps) => React.ReactNode
}
export type PrivateRouteProviderConfig = PrivateRouteContextState

const PrivateRouteContext = React.createContext<PrivateRouteContextState>({
  isAuthenticated: false,
  getAllPermissions: () => [],
  notAuthenticatedComponent: () => null,
  notAuthorizedComponent: () => null,
})

export function usePrivateRouteContext() {
  const ctx = useContext(PrivateRouteContext)
  if (ctx === null)
    throw new Error(
      "PrivateRoute/usePrivateRouteContext can only be used within a PrivateRouteProvider.",
    )

  return ctx
}

interface PrivateRouteProviderProps extends PropsWithChildren<object> {
  config: PrivateRouteProviderConfig
}

export const PrivateRouteProvider: React.FC<PrivateRouteProviderProps> = ({
  children,
  config,
}) => {
  return (
    <PrivateRouteContext.Provider value={config}>
      {children}
    </PrivateRouteContext.Provider>
  )
}
